import { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Icon, {
	ICON_EXPLORE,
	ICON_DEALER,
	ICON_CASINO_SEVEN,
	ICON_HEART,
	ICON_SPORTSBOOK
} from '~/components/icon/index.js';
import useTranslations from '~/components/translation/use-translations.ts';
import { selectProducts, updateMainContentLoading } from '~/components/brand/ducks/index.js';
import { selectCasinoMaintenanceEnabled } from '~/components/brand/ducks/selectors.js';

import useCasinoRoutes from '~/components/router/use-casino-routes';
import { ROUTE_SPORTSBOOK } from '~/components/router/constants.js';
import HeaderSelector from '../header/header-selector.js';
import { TabBar, TabItem } from '../tab-bar.js';

const textKeys = [
	'MOBILE_CASINO_BUTTON',
	'MOBILE_EXPLORE_BUTTON',
	'MOBILE_ALL_GAMES_BUTTON',
	'MOBILE_LIVE_CASINO_BUTTON',
	'MOBILE_MY_GAMES_TITLE',
	'MOBILE_SPORTSBOOK_BUTTON'
];

const CasinoTabs = memo(() => {
	const [availableTabs, setAvailableTabs] = useState([]);
	const dispatch = useDispatch();
	useEffect(() => {
		dispatch(updateMainContentLoading('loaded'));
	}, [dispatch]);
	const products = useSelector(selectProducts);
	const {
		translations: {
			MOBILE_CASINO_BUTTON,
			MOBILE_EXPLORE_BUTTON,
			MOBILE_ALL_GAMES_BUTTON,
			MOBILE_MY_GAMES_TITLE,
			MOBILE_LIVE_CASINO_BUTTON,
			MOBILE_SPORTSBOOK_BUTTON
		}
	} = useTranslations(textKeys);
	const { pathname } = useLocation();
	const isCasinoMaintenanceEnabled = useSelector(selectCasinoMaintenanceEnabled);
	const {
		ROUTE_CASINO,
		ROUTE_CASINO_ALL_GAMES,
		ROUTE_CASINO_EXPLORE,
		ROUTE_CASINO_MY_GAMES,
		ROUTE_LIVE_CASINO,
		ROUTE_LIVE_CASINO_EXPLORE
	} = useCasinoRoutes();

	const CASINO_ROUTES = useMemo(() => [ROUTE_CASINO, ROUTE_CASINO_EXPLORE], [ROUTE_CASINO, ROUTE_CASINO_EXPLORE]);
	const LIVE_CASINO_ROUTES = useMemo(
		() => [ROUTE_LIVE_CASINO, ROUTE_LIVE_CASINO_EXPLORE],
		[ROUTE_LIVE_CASINO, ROUTE_LIVE_CASINO_EXPLORE]
	);

	useEffect(() => {
		const casinoTab = (
			<TabItem
				Icon={
					<Icon
						type={products.includes('sportsbook') ? ICON_CASINO_SEVEN : ICON_EXPLORE}
						className="u-div-c"
					/>
				}
				key="mobileCasinoButton"
				label={products.includes('sportsbook') ? MOBILE_CASINO_BUTTON : MOBILE_EXPLORE_BUTTON}
				href={ROUTE_CASINO_EXPLORE}
				className="js-tab-section1 tab-explore"
				aria-selected={CASINO_ROUTES.includes(pathname) ? 'true' : 'false'}
			/>
		);

		const allGamesTab = (
			<TabItem
				Icon={<Icon type={ICON_CASINO_SEVEN} className=" u-div-c" />}
				key="mobileAllGamesButton"
				label={MOBILE_ALL_GAMES_BUTTON}
				href={ROUTE_CASINO_ALL_GAMES}
				className="js-tab-section1 tab-explore"
				aria-selected={pathname.includes(ROUTE_CASINO_ALL_GAMES) ? 'true' : 'false'}
			/>
		);

		const myGamesTab = (
			<TabItem
				Icon={<Icon type={ICON_HEART} className="u-div-c" />}
				key="mobileMyGamesTitle"
				label={MOBILE_MY_GAMES_TITLE}
				href={ROUTE_CASINO_MY_GAMES}
				className="js-tab-section1 tab-explore"
				aria-selected={pathname.includes(ROUTE_CASINO_MY_GAMES)}
			/>
		);

		const liveCasinoTab = (
			<TabItem
				Icon={<Icon type={ICON_DEALER} className="u-div-c" />}
				key="mobileLiveCasinoButton"
				label={MOBILE_LIVE_CASINO_BUTTON}
				href={ROUTE_LIVE_CASINO_EXPLORE}
				className="js-tab-section1 tab-explore"
				aria-selected={LIVE_CASINO_ROUTES.includes(pathname) ? 'true' : 'false'}
			/>
		);

		const sportsbookTab = (
			<TabItem
				Icon={<Icon type={ICON_SPORTSBOOK} className="u-div-c" />}
				key="mobileSportsbookButton"
				label={MOBILE_SPORTSBOOK_BUTTON}
				href={ROUTE_SPORTSBOOK}
				className="js-tab-section1 tab-explore"
				aria-selected={pathname.includes(ROUTE_SPORTSBOOK) ? 'true' : 'false'}
			/>
		);

		const liveCasino = products.includes('livecasino') ? liveCasinoTab : null;

		if (products.includes('sportsbook')) {
			setAvailableTabs([casinoTab, liveCasino, sportsbookTab]);
			return;
		}
		setAvailableTabs([casinoTab, allGamesTab, myGamesTab, liveCasino]);
	}, [
		products,
		pathname,
		MOBILE_CASINO_BUTTON,
		MOBILE_EXPLORE_BUTTON,
		MOBILE_ALL_GAMES_BUTTON,
		MOBILE_MY_GAMES_TITLE,
		MOBILE_LIVE_CASINO_BUTTON,
		MOBILE_SPORTSBOOK_BUTTON,
		ROUTE_CASINO_EXPLORE,
		CASINO_ROUTES,
		ROUTE_CASINO_ALL_GAMES,
		ROUTE_CASINO_MY_GAMES,
		ROUTE_LIVE_CASINO_EXPLORE,
		LIVE_CASINO_ROUTES
	]);

	return (
		<>
			<HeaderSelector hasBottomOffset={!isCasinoMaintenanceEnabled} />
			{!isCasinoMaintenanceEnabled && <TabBar dataAt="casino-tabs-mobile">{availableTabs}</TabBar>}
		</>
	);
});

export default CasinoTabs;
